import React, { type ReactNode } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';

import type { SearchFederatedResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type { searchFederatedEmail_AtlassianSearchPage$key } from './__generated__/searchFederatedEmail_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	nameContainer: {
		whiteSpace: 'nowrap',
	},
});

export const SearchFederatedEmail = ({
	node,
	position,
	product,
	icon,
	cloudId,
	appMode,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	mainContentWidth,
}: SearchFederatedResultProps<searchFederatedEmail_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchFederatedEmail_AtlassianSearchPage on SearchResultFederated {
				__typename
				id
				title
				url
				lastModifiedDate
				description
				entity {
					... on SearchFederatedEmailEntity {
						sender {
							name
						}
					}
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { id, lastModifiedDate, url, title, description, entity } = data;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} message={messages.timestampLabel} />
	) : null;

	const label = <FormattedMessage {...messages.emailLabel} />;
	const inboxLabel = <FormattedMessage {...messages.inboxLabel} />;
	const fullName = entity?.sender?.name || '';
	const firstName = fullName?.split(' ')?.[0] || formatMessage(messages.externalPersonLabel);
	const actionLabel = formatMessage(messages.sentLabel, { person: firstName });

	const metadataParts: [string, ReactNode][] = [
		['label', label],
		['inbox', inboxLabel],
		['lastModified', lastModifiedComponent],
		[
			'sender',
			<Inline space="space.025" alignBlock="center">
				<ProfileAvatar name={fullName} />
				<Box xcss={styles.nameContainer}>{actionLabel}</Box>
			</Inline>,
		],
	];

	return (
		<SearchResult
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			headerMetadataParts={metadataParts}
			title={enrichExcerpt(title)}
			url={url}
			description={<DescriptionBody description={processExcerpt(description)} />}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			isThirdParty
			mainContentWidth={mainContentWidth}
		/>
	);
};
